import React, { useState, useEffect, useRef } from 'react';
import { Send, X, MessageCircle, User, Loader2, PhoneOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import toast from 'react-hot-toast';

/**
 * Customer Chat Interface Component
 * Real-time chat interface for customers to chat with agents
 */
const CustomerChatInterface = ({ chatSession, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingIndicator, setTypingIndicator] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    // Ensure chatSessionId is available before connecting
    if (!chatSession?.chatSessionId) {
      console.warn('Chat Session ID not available. Cannot connect to WebSocket.');
      return;
    }

    const chatSessionId = chatSession.chatSessionId;
    //console.log('Chat Session ID:', chatSessionId);

    // Set initial welcome message
    setMessages([
      {
        messageId: 'welcome',
        senderType: 'SYSTEM',
        senderName: 'System',
        content: 'Your chat request has been sent. An agent will join shortly. Please wait...',
        messageType: 'INFO',
        timestamp: Date.now()
      }
    ]);

    // Connect to WebSocket for guest (no authentication required)
    // Use /ws/chat endpoint with chatSessionId query parameter
    // Use the same BASE_URL logic from apiClient.js for consistency
    const getBaseUrl = () => {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      
      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        return 'http://localhost:8080';
      } else {
        return `${protocol}//${hostname}`;
      }
    };
    
    const BASE_URL = getBaseUrl();
    
    // For WebSocket, use gateway URL
    // Convert HTTP/HTTPS URL to WebSocket URL (ws:// or wss://)
    const NOTIFICATION_SERVICE_URL = `${BASE_URL}/gateway/hd-notification`;
    
    // Convert HTTP/HTTPS to WebSocket protocol
    const wsUrl = NOTIFICATION_SERVICE_URL
      .replace(/^http:\/\//, 'ws://')
      .replace(/^https:\/\//, 'wss://');
    
    // Connect to /ws/chat?chatSessionId={chatSessionId}
    const chatSocketUrl = `${wsUrl}/ws/chat?chatSessionId=${chatSessionId}`;
    //console.log('Connecting to WebSocket...');
    //console.log('WebSocket URL:', chatSocketUrl);

    try {
      // Create native WebSocket connection
      const ws = new WebSocket(chatSocketUrl);
      wsRef.current = ws;

      ws.onopen = (event) => {
        //console.log('Guest WebSocket connected:', event);
        setConnectionStatus('connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          //console.log('Received chat message:', data);

          if (data.messageType === 'MESSAGE') {
            setMessages((prev) => {
              // First check by messageId (exact match)
              const existsById = prev.find((msg) => msg.messageId === data.messageId);
              if (existsById) return prev;
              
              // For GUEST messages, check if we have a temp message with same content
              // This prevents duplicates when we add optimistically and then receive server echo
              if (data.senderType === 'GUEST') {
                const now = Date.now();
                const tempMessageIndex = prev.findIndex((msg) => {
                  // Check if it's a temp message with same content and senderType
                  // and was added within the last 3 seconds
                  return msg.messageId?.startsWith('temp-') &&
                         msg.content === data.content &&
                         msg.senderType === 'GUEST' &&
                         msg.senderName === data.senderName &&
                         (now - (msg.timestamp || 0)) < 3000;
                });
                
                if (tempMessageIndex !== -1) {
                  // Replace temp message with real one from server
                  const updated = [...prev];
                  updated[tempMessageIndex] = data;
                  return updated;
                }
              }
              
              // For other cases, check by content + sender + timestamp (within 2 seconds)
              // This catches any other duplicates
              const existsByContent = prev.find((msg) => {
                const timeDiff = Math.abs((msg.timestamp || 0) - (data.timestamp || 0));
                return msg.content === data.content &&
                       msg.senderType === data.senderType &&
                       msg.senderName === data.senderName &&
                       timeDiff < 2000;
              });
              if (existsByContent) return prev;
              
              return [...prev, data];
            });
            setTypingIndicator(null);
          } else if (data.messageType === 'LEFT' || data.messageType === 'ENDED' || data.messageType === 'CLOSED') {
            // Agent has ended the chat session
            //console.log('Chat ended by agent:', data);
            
            // Add the end message to chat history
            setMessages((prev) => {
              const exists = prev.find((msg) => msg.messageId === data.messageId);
              if (exists) return prev;
              return [...prev, {
                ...data,
                messageType: 'LEFT',
                senderType: 'SYSTEM',
                senderName: data.senderName || 'System',
                content: data.content || 'Agent has ended the chat session. Thank you for contacting us!',
              }];
            });
            
            // Set connection status to disconnected
            setConnectionStatus('disconnected');
            
            // Show notification to customer
            toast('Chat session has been ended by the agent', {
              icon: 'ℹ️',
              duration: 4000,
            });
            
            // Close WebSocket connection after a short delay
            setTimeout(() => {
              if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.close(1000, 'Chat ended by agent');
              }
            }, 2000);
            
            // Clear typing indicator
            setTypingIndicator(null);
          } else if (data.messageType === 'TYPING' && data.senderType === 'AGENT') {
            setTypingIndicator({
              senderName: data.senderName,
              senderType: data.senderType,
            });
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            typingTimeoutRef.current = setTimeout(() => {
              setTypingIndicator(null);
            }, 3000);
          } else if (data.messageType === 'JOINED') {
            setMessages((prev) => {
              const exists = prev.find((msg) => msg.messageId === data.messageId);
              if (exists) return prev;
              return [...prev, data];
            });
          }
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
        toast.error('Connection error. Please refresh the page.');
      };

      ws.onclose = (event) => {
        //console.log('Guest WebSocket closed:', event.code, event.reason);
        setConnectionStatus('disconnected');
      };

      // Cleanup on unmount
      return () => {
        //console.log('Cleaning up WebSocket connection...');
        
        // Close WebSocket connection
        if (wsRef.current) {
          wsRef.current.close(1000, 'Component unmounting');
          wsRef.current = null;
        }
        
        // Clear typing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      };
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
      setConnectionStatus('error');
      toast.error('Failed to connect to chat. Please try again.');
    }
  }, [chatSession?.chatSessionId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typingIndicator]);

  /**
   * Handle sending a message
   */
  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !chatSession?.chatSessionId) {
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error('Not connected. Please wait...');
      return;
    }

    const messageContent = inputMessage.trim();
    setInputMessage('');

    try {
      // Use wrapper format: {type: "...", payload: {...}}
      // Ensure senderName is set from chatSession (should come from InitiateChatResponse)
      const senderName = chatSession?.guestName || chatSession?.data?.guestName || 'Guest';
      // Get userId from sessionStorage and use it as senderId
      const userId = sessionStorage.getItem('userId');
      //console.log('Sending message with senderName:', senderName, 'senderId:', userId, 'chatSession:', chatSession);
      
      const message = {
        type: 'CHAT_MESSAGE',
        payload: {
          chatSessionId: chatSession.chatSessionId,
          content: messageContent,
          senderType: 'GUEST',
          senderName: senderName, // Use guestName from chatSession
          senderId: userId || null, // Use userId from sessionStorage
          messageType: 'MESSAGE',
          timestamp: Date.now(),
        }
      };

      //console.log('Sending WebSocket message (wrapper format):', message);
      wsRef.current.send(JSON.stringify(message));

      // Add message to local state immediately for better UX
      setMessages((prev) => [...prev, {
        ...message.payload,
        messageId: `temp-${Date.now()}`,
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      setInputMessage(messageContent); // Restore message if failed
    }
  };

  /**
   * Handle typing indicator
   */
  const handleTyping = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!isTyping && inputMessage.trim()) {
      setIsTyping(true);
      try {
        // Use wrapper format: {type: "...", payload: {...}}
        // Ensure senderName is set from chatSession (should come from InitiateChatResponse)
        const senderName = chatSession?.guestName || chatSession?.data?.guestName || 'Guest';
        // Get userId from sessionStorage and use it as senderId
        const userId = sessionStorage.getItem('userId');
        
        const typingMessage = {
          type: 'TYPING',
          payload: {
            chatSessionId: chatSession.chatSessionId,
            senderType: 'GUEST',
            senderName: senderName, // Use guestName from chatSession
            senderId: userId || null, // Use userId from sessionStorage
            timestamp: Date.now(),
          }
        };
        wsRef.current.send(JSON.stringify(typingMessage));
      } catch (error) {
        console.error('Error sending typing indicator:', error);
      }
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  /**
   * Format timestamp for display
   */
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  /**
   * Handle ending the chat session (customer-initiated)
   */
  const handleEndChat = async () => {
    if (!chatSession?.chatSessionId) {
      toast.error('Chat session ID not available');
      return;
    }

    // Confirm with user
    if (!window.confirm('Are you sure you want to end this chat session?')) {
      return;
    }

    try {
      const senderName = chatSession?.guestName || chatSession?.data?.guestName || 'Guest';
      const senderId = sessionStorage.getItem('userId');

      // Send END_SESSION message via WebSocket
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const endSessionMessage = {
          type: 'END_SESSION',
          payload: {
            chatSessionId: chatSession.chatSessionId,
            senderType: 'GUEST',
            senderName: senderName,
            senderId: senderId || null,
            timestamp: Date.now(),
          }
        };
        
        wsRef.current.send(JSON.stringify(endSessionMessage));
        //console.log('END_SESSION message sent:', endSessionMessage);
      }

      toast.success('Chat session ended. Thank you for contacting us!');
      
      // Update UI to show chat ended
      setConnectionStatus('disconnected');
      
      // Add end message to chat history
      setMessages((prev) => [...prev, {
        messageId: `end-${Date.now()}`,
        senderType: 'SYSTEM',
        senderName: 'System',
        content: 'You have ended the chat session. Thank you for contacting us!',
        messageType: 'ENDED',
        timestamp: Date.now(),
      }]);

      // Close WebSocket connection after a short delay
      setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close(1000, 'Chat ended by customer');
        }
      }, 2000);
    } catch (error) {
      console.error('Error ending chat:', error);
      toast.error('Failed to end chat session. Please try again.');
    }
  };

  if (!chatSession) {
    return null;
  }

  return (
    <Card className="flex flex-col h-[min(600px,calc(100vh-8rem))] fixed bottom-24 right-4 sm:bottom-28 sm:right-6 w-96 z-[60] shadow-2xl">
      <CardHeader className="flex-shrink-0 border-b border-gray-200 bg-blue-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Chat with Agent</CardTitle>
              <p className="text-xs text-white/80">
                {connectionStatus === 'connected' ? 'Connected' : 
                 connectionStatus === 'connecting' ? 'Connecting...' : 
                 connectionStatus === 'error' ? 'Connection Error' : 'Disconnected'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {connectionStatus === 'connected' && (
              <Button
                onClick={handleEndChat}
                variant="outline"
                size="sm"
                className="h-8 px-3 bg-red-500 hover:bg-red-600 text-white border-red-400 hover:border-red-500 font-medium"
                title="End Chat"
              >
                <PhoneOff className="h-4 w-4 mr-1" />
                <span className="text-xs">End Chat</span>
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {chatSession.ticketNumber && (
          <div className="mt-2">
            <Badge variant="outline" className="text-xs bg-white/20 text-white border-white/30">
              Ticket: {chatSession.ticketNumber}
            </Badge>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {connectionStatus === 'connecting' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-sm text-gray-600">Connecting to agent...</span>
            </div>
          )}

          {connectionStatus === 'disconnected' && (
            <div className="flex items-center justify-center py-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 text-center">
                <p className="text-sm text-yellow-800 font-medium">
                  Chat session has ended
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  The agent has closed this chat. You can start a new chat if needed.
                </p>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.messageId || `msg-${message.timestamp}`}
              className={`flex ${
                message.senderType === 'GUEST' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[70%] rounded-lg px-4 py-2 ${
                  message.senderType === 'GUEST'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}
              >
                {message.senderType === 'AGENT' && (
                  <div className="text-xs font-semibold mb-1 opacity-75">
                    {message.senderName || 'Agent'}
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p className="text-xs mt-1 opacity-75">
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {typingIndicator && typingIndicator.senderType === 'AGENT' && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                <p className="text-sm text-gray-500 italic">
                  {typingIndicator.senderName || 'Agent'} is typing...
                </p>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 p-4 flex-shrink-0 bg-white">
          <form onSubmit={handleSendMessage} className="flex items-center gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                handleTyping();
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder={
                connectionStatus === 'connected' 
                  ? "Type your message..." 
                  : connectionStatus === 'disconnected'
                  ? "Chat session ended"
                  : "Connecting..."
              }
              disabled={connectionStatus !== 'connected'}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <Button
              type="submit"
              disabled={!inputMessage.trim() || connectionStatus !== 'connected'}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomerChatInterface;
